import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import * as dat from 'dat.gui'
import Stats from 'stats.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'

import { VertexNormalsHelper } from 'three/addons/helpers/VertexNormalsHelper.js';

//=============================================================================
// CONFIGURATION
//=============================================================================
const clock = new THREE.Clock()

const bgColor = new THREE.Color(0x101733); // bluish purple

const CONFIG = {
  isMobile: /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
  enableStats: true,
  enableGui: true,
  enablePostProcessing: false,
  antialias: true,
  shadows: true,
  pixelRatio: window.devicePixelRatio
}

const customUniforms = {
    uTime: { value: 0 },
    uWindSpeed: { value: 1.2 },
    uWindBendFactor: { value: 0.55 },
    uVisualizeWindNoise: { value: 0.0 },
    uWindNoiseAZoom : { value: new THREE.Vector2(0.2,0.2) },
    uWindNoiseBZoom : { value: new THREE.Vector2(0.5,0.5) },
    uRotationAngle: { value: 0.0 },
    grassOffsetX: {value: 10.0},
    grassOffsetY: {value: 4.75},
    uHeightScale: {value: 0.05}
}
// const customUniforms = {
//     uTime: { value: 0 },
//     uWindSpeed: { value: 0.75 },
//     uWindBendFactor: { value: 0.95 },
//     uVisualizeWindNoise: { value: 0.0 },
//     uWindNoiseAZoom : { value: new THREE.Vector2(0.2,0.2) },
//     uWindNoiseBZoom : { value: new THREE.Vector2(0.5,0.5) },
//     uRotationAngle: { value: 0.0 },
//     grassOffsetX: {value: 10.0},
//     grassOffsetY: {value: 4.75},
//     uHeightScale: {value: 0.05}
// }

//=============================================================================
// LOADING MANAGER
//=============================================================================

class LoadingManager {
  constructor() {
    this.overlay = document.getElementById('loading-overlay')
    this.progressBar = document.getElementById('progress-bar')
    this.loadingText = document.getElementById('loading-text')
    this.totalAssets = 0
    this.loadedAssets = 0
  }

  setTotal(total) {
    this.totalAssets = total
  }

  increment(message = 'Loading...') {
    this.loadedAssets++
    if (this.loadingText) this.loadingText.textContent = message
    if (this.progressBar) {
      this.progressBar.style.width = `${(this.loadedAssets / this.totalAssets) * 100}%`
    }
    
    if (this.loadedAssets >= this.totalAssets) {
      this.complete()
    }
  }

  complete() {
    if (this.overlay) {
      this.overlay.classList.add('hidden')
    }
  }
}

//=============================================================================
// LOADERS
//=============================================================================

class AssetLoader {
  constructor() {
    this.textureLoader = new THREE.TextureLoader()
    this.gltfLoader = new GLTFLoader()
    this.cubeTextureLoader = new THREE.CubeTextureLoader()
  }

  loadTexture(path, onLoad) {
    return this.textureLoader.load(path, onLoad)
  }

  loadGLTF(path, onLoad) {
    return this.gltfLoader.load(path, onLoad)
  }

  loadCubeMap(paths, onLoad) {
    return this.cubeTextureLoader.load(paths, onLoad)
  }
}

//=============================================================================
// CAMERA CONTROLLER
//=============================================================================

class CameraController {
  constructor(camera, options = {}) {
    this.camera = camera
    this.moveSpeed = options.moveSpeed || 15
    this.rotationSpeed = options.rotationSpeed || Math.PI * 0.2
    this.keysPressed = {}
    this.mouse = { x: 0, y: 0 }
    this.camera.rotation.order = 'YXZ'; // IMPORTANT
    this.camTarget = new THREE.Vector3(-5, 8, -25);

    this.yaw = 0;
    this.pitch = 0;

    this.maxYaw = 0.5; // Limit how far you can look left/right
    this.maxPitch = 0.6; // Limit how far you can look up/down (0.6 ~ 35 degrees)

    this.lookDistance = 10;
    
    // Boundary limits
    this.bounds = options.bounds || {
      minX: -Infinity, maxX: Infinity,
      minZ: -Infinity, maxZ: Infinity
    }

    this.setupControls()
  }

  setupControls() {
    window.addEventListener('keydown', (event) => {
      const key = event.key.toLowerCase()
      this.keysPressed[key] = true

      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
        event.preventDefault()
      }
    })

    window.addEventListener('keyup', (event) => {
      const key = event.key.toLowerCase()
      delete this.keysPressed[key]
    })

    window.addEventListener('pointermove', (event) => {
      this.mouse.x = (event.clientX / window.innerWidth  - 0.5) * 2;
      this.mouse.y = (event.clientY / window.innerHeight - 0.5) * 2;
    })
  }

  update(delta) {
    const actualMoveSpeed = this.moveSpeed * delta
    const actualRotationSpeed = this.rotationSpeed * delta
    const velocity = new THREE.Vector3(0, 0, 0)

    // Movement
    if (this.keysPressed['w'] || this.keysPressed['arrowup']) velocity.z -= actualMoveSpeed
    if (this.keysPressed['s'] || this.keysPressed['arrowdown']) velocity.z += actualMoveSpeed
    if (this.keysPressed['a']) velocity.x -= actualMoveSpeed
    if (this.keysPressed['d']) velocity.x += actualMoveSpeed

    this.camera.translateX(velocity.x)
    this.camera.translateZ(velocity.z)

    // Rotation
    if (this.keysPressed['arrowleft']) this.camera.rotation.y += actualRotationSpeed
    if (this.keysPressed['arrowright']) this.camera.rotation.y -= actualRotationSpeed
    if (this.keysPressed['q']) this.camera.rotation.y += actualRotationSpeed * 4.5
    if (this.keysPressed['e']) this.camera.rotation.y -= actualRotationSpeed * 4.5

    // Boundary clamping
    this.camera.position.x = Math.max(this.bounds.minX, Math.min(this.bounds.maxX, this.camera.position.x))
    this.camera.position.z = Math.max(this.bounds.minZ, Math.min(this.bounds.maxZ, this.camera.position.z))

    // --- compute target yaw + pitch ---
    this.targetYaw = THREE.MathUtils.clamp(this.mouse.x * this.maxYaw, -this.maxYaw, this.maxYaw);

    const rawPitch = THREE.MathUtils.clamp(-this.mouse.y * this.maxPitch, -this.maxPitch, this.maxPitch);
    const levelFactor = Math.cos(this.targetYaw);
    this.targetPitch = rawPitch * levelFactor;

    // --- smooth yaw and pitch (yours is fine) ---
    const smoothing = Math.min(1.0, 6.0 * delta);
    this.yaw   += (this.targetYaw   - this.yaw)   * smoothing;
    this.pitch += (this.targetPitch - this.pitch) * smoothing;

    // clamp pitch so it NEVER flips
    this.pitch = THREE.MathUtils.clamp(this.pitch, -1.2, 1.2);

    // ----------- BUILD DIRECTION SAFELY -----------
    const dir = new THREE.Vector3();

    // forward in THREE is -Z
    dir.set(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch)
    );

    // fallback if somehow zero
    if (!isFinite(dir.x) || !isFinite(dir.y) || !isFinite(dir.z)) {
      dir.set(0, 0, -1);
    }

    dir.normalize();

    // ----------- SET TARGET -----------
    this.camTarget.copy(this.camera.position).addScaledVector(dir, 10);

    // ----------- LOOK AT TARGET -----------
    this.camera.lookAt(this.camTarget);

  }
}

//=============================================================================
// SCENE MANAGER
//=============================================================================

class SceneManager {
  constructor() {
    this.canvas = document.querySelector('canvas.webgl')
    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight
    }
    
    this.init()
    this.setupHelpers()
    this.setupLights()
    this.setupPostProcessing()
    this.setupEventListeners()
  }

  init() {
    // Scene
    this.scene = new THREE.Scene()
    
    // Clock
    this.clock = new THREE.Clock()
    
    // Camera
    this.camera = new THREE.PerspectiveCamera(
      CONFIG.isMobile ? 90 : 75,
      this.sizes.width / this.sizes.height,
      0.1,
      300
    )
    this.camera.position.set(0, 3, 20)
    this.camera.lookAt(-5, 8, -25)
    // this.camera.rotateX(Math.PI/10);
    // this.camera.rotateZ(Math.PI/10);
    this.scene.add(this.camera)

    this.scene.background = bgColor;
    this.scene.fog = new THREE.FogExp2(new THREE.Color(0xf5c38b), 0.05)

    
    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: CONFIG.antialias
    })
    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(Math.min(CONFIG.pixelRatio, 2))
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.renderer.toneMapping = THREE.ReinhardToneMapping
    this.renderer.toneMappingExposure = 1
    
    if (CONFIG.shadows) {
      this.renderer.shadowMap.enabled = true
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    }

    // Controls (optional - comment out if using custom camera controller)
    // this.controls = new OrbitControls(this.camera, this.canvas)
    // this.controls.enableDamping = true
  }

  setupHelpers() {
    // Stats
    if (CONFIG.enableStats) {
      this.stats = new Stats()
      this.stats.showPanel(0)
      document.body.appendChild(this.stats.dom)
    }

    // GUI
    if (CONFIG.enableGui) {
      this.gui = new dat.GUI()
      this.gui.close()
            
      //gui for instanced mesh
      const instancedMeshFolder = this.gui.addFolder('Instanced Mesh')
      //tick for wireframe gui
      // instancedMeshFolder.add(planeInstanceShaderMaterial, 'wireframe').name('Wireframe')
      instancedMeshFolder.add(customUniforms.grassOffsetX, 'value').min(0).max(10).step(0.001).name('offset x').onChange(()=>{
        
        const aHeight = app.grass.geometry.getAttribute("a_bladeCordinateZ");
        const aPosY = app.grass.geometry.getAttribute("a_bladeCordinateY");
        const aPosX = app.grass.geometry.getAttribute("a_bladeCordinateX");        

        for (let i = 0; i < aHeight.count; i++) {
          const x = aPosX.array[i];
          const y = aPosY.array[i];
          aHeight.array[i] = Math.sin((x / 5.0) + customUniforms.grassOffsetX.value) * 1.0  
                                  + Math.cos((y / 4.0) + customUniforms.grassOffsetY.value) * 0.5;
        }

        aHeight.needsUpdate = true;

      })
      instancedMeshFolder.add(customUniforms.grassOffsetY, 'value').min(0).max(10).step(0.001).name('offset y').onChange(()=>{
        
        const aHeight = app.grass.geometry.getAttribute("a_bladeCordinateZ");
        const aPosY = app.grass.geometry.getAttribute("a_bladeCordinateY");
        const aPosX = app.grass.geometry.getAttribute("a_bladeCordinateX");        

        for (let i = 0; i < aHeight.count; i++) {
          const x = aPosX.array[i];
          const y = aPosY.array[i];
          aHeight.array[i] = Math.sin((x / 5.0) + customUniforms.grassOffsetX.value) * 1.0  
                                  + Math.cos((y / 4.0) + customUniforms.grassOffsetY.value) * 0.5;
        }

        aHeight.needsUpdate = true;

      })
      instancedMeshFolder.add(customUniforms.uHeightScale, 'value').min(0).max(5).step(0.001).name('height scale')
      instancedMeshFolder.add(customUniforms.uWindSpeed, 'value').min(0).max(3).step(0.001).name('Wind Speed')
      instancedMeshFolder.add(customUniforms.uWindBendFactor, 'value').min(0).max(3).step(0.001).name('wind Bend Factor')
      instancedMeshFolder.add(customUniforms.uVisualizeWindNoise, 'value').min(0).max(1).step(1).name('Visualize Wind Noise')
      instancedMeshFolder.add(customUniforms.uWindNoiseAZoom.value, 'x').min(-3).max(3).step(0.001).name('Wind Noise A Zoom x')
      instancedMeshFolder.add(customUniforms.uWindNoiseAZoom.value, 'y').min(-3).max(3).step(0.001).name('Wind Noise A Zoom y')
      instancedMeshFolder.add(customUniforms.uWindNoiseBZoom.value, 'y').min(-3).max(3).step(0.001).name('Wind Noise B Zoom x')
      instancedMeshFolder.add(customUniforms.uWindNoiseBZoom.value, 'x').min(-3).max(3).step(0.001).name('Wind Noise B Zoom y')
      instancedMeshFolder.add(customUniforms.uRotationAngle, 'value').min(0).max(2 * 3.1457).step(0.001).name('blade Rotation Angle')
    }
  }

  setupLights() {
    // Ambient Light
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5)
    this.scene.add(ambientLight)

    // Directional Light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 7)
    directionalLight.position.set(5, 10, 5)
    directionalLight.castShadow = CONFIG.shadows
    
    if (CONFIG.shadows) {
      directionalLight.shadow.camera.far = 35
      directionalLight.shadow.camera.left = -10
      directionalLight.shadow.camera.right = 10
      directionalLight.shadow.camera.top = 10
      directionalLight.shadow.camera.bottom = -10
      directionalLight.shadow.mapSize.set(1024, 1024)
    }

    this.dirLight = directionalLight;
    
    this.scene.add(directionalLight)
  }

  setupPostProcessing() {
    if (!CONFIG.enablePostProcessing) return

    this.composer = new EffectComposer(this.renderer)
    this.composer.outputEncoding = THREE.sRGBEncoding
    
    // Render Pass
    const renderPass = new RenderPass(this.scene, this.camera)
    this.composer.addPass(renderPass)
    
    // Bloom Pass (optional)
    if (!CONFIG.isMobile) {
      this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(this.sizes.width, this.sizes.height),
        0.2,  // strength
        0.3,  // radius
        0.25  // threshold
      )
      this.composer.addPass(this.bloomPass)
    }
  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.onResize())
  }

  onResize() {
    this.sizes.width = window.innerWidth
    this.sizes.height = window.innerHeight

    this.camera.aspect = this.sizes.width / this.sizes.height
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(Math.min(CONFIG.pixelRatio, 2))

    if (this.composer) {
      this.composer.setSize(this.sizes.width, this.sizes.height)
    }
  }

  render() {
    if (this.composer && CONFIG.enablePostProcessing) {
      this.composer.render()
    } else {
      this.renderer.render(this.scene, this.camera)
    }
  }

  update() {
    const delta = Math.max(0.001, Math.min(this.clock.getDelta(), 0.1))
    
    if (this.controls) {
      this.controls.update()
    }
    
    return delta
  }
}

//=============================================================================
// MAIN APPLICATION
//=============================================================================

class App {
  constructor() {
    this.loadingManager = new LoadingManager()
    this.assetLoader = new AssetLoader()
    this.sceneManager = new SceneManager()
    this.grassMat = this.createGrassMaterial();
    this.fireflyMat = this.createFireflyMaterial();
    this.fireflies = null;
    
    // Optional: Custom camera controller
    this.cameraController = new CameraController(this.sceneManager.camera)

    window.addEventListener("toggleNight", () => {

      const dirLightStart = this.sceneManager.dirLight.intensity;
      const dirLightEnd = dirLightStart === 2.5 ? 7 : 2.5;

      const startNight = this.skyMaterial.uniforms.uNight.value;
      const endNight = startNight === 0 ? 1 : 0;

      const startSun = this.skyMaterial.uniforms.sunDir.value.clone();
      const endSun = endNight
        ? new THREE.Vector3(0.0, 5.7, -10.0).normalize()
        : new THREE.Vector3(0.0, -0.7, -10.0).normalize();

      const startFog = this.sceneManager.scene.fog.color.clone();
      const endFog = endNight
        ? new THREE.Color(0x090d1d)
        : new THREE.Color(0xf5c38b);

      const startFly = this.fireflyMat.uniforms.uButterfly.value;
      const endFly = startFly === 0 ? 1 : 0; //0->firefly 1->butterfly

        if(startFly)
        {
            this.fireflyMat.transparent = true;
            this.fireflyMat.depthWrite = false;
            this.fireflyMat.blending = THREE.AdditiveBlending;
        }
        else
        {
            this.fireflyMat.transparent = true;
            this.fireflyMat.depthWrite = true;
            this.fireflyMat.blending = THREE.NormalBlending;
        }

        this.fireflyMat.needsUpdate = true;

      const duration = 1000;
      const startTime = performance.now();

      const animate = (time) => {

        const t = Math.min((time - startTime) / duration, 1);

        // smoothstep easing
        const k = t * t * (3 - 2 * t);

        // animate night uniform
        this.skyMaterial.uniforms.uNight.value =
          startNight + (endNight - startNight) * k;

        this.fireflyMat.uniforms.uButterfly.value =
          startFly + (endFly - startFly) * k;

        this.sceneManager.dirLight.intensity =
          dirLightStart + (dirLightEnd - dirLightStart) * k;

        // animate sun direction
        this.skyMaterial.uniforms.sunDir.value.lerpVectors(startSun, endSun, k);

        // animate fog color
        this.sceneManager.scene.fog.color.lerpColors(startFog, endFog, k);

        if (t < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    });
    
    this.init()
  }
  
  createGrassBladeGeometry(bladeWidth, bladeHeight, ySegments = 10) {
    const geometry = new THREE.BufferGeometry();
  
    const positions = [];
    const uvs = [];
    const indices = [];
  
    const xSegments = 2; // 3 vertices per row: left, center, right
  
    for (let i = 0; i <= ySegments; i++) {
        const t = i / ySegments;
        const y = t * bladeHeight;
        // const halfWidth = (1 - t) * bladeWidth * 0.5; // Tapering towards the tip

        // More natural tapering using cosine: starts wider, narrows quickly
        // const taper = Math.cos(t * Math.PI * 0.5); // from 1 to 0
        // const halfWidth = taper * bladeWidth * 0.5;

        const taper = Math.cos((t * Math.PI * 0.75) - Math.PI * 0.25) ; // from 1 to 0
        const halfWidth = taper * bladeWidth * 0.5;

        // Front face vertices (left and right)
        positions.push(-halfWidth, y, 0); // Left
        positions.push(halfWidth, y, 0);  // Right
    }

    // Duplicate front vertices to create separate back face vertices
    const frontVertexCount = positions.length / 3;
    for (let i = 0; i < frontVertexCount; i++) {
        const idx = i * 3;
        positions.push(positions[idx], positions[idx + 1], positions[idx + 2]);
    }

    // Build indices for front and back faces
    const vertsPerRow = 2;
    for (let i = 0; i < ySegments; i++) {
        const a = i * vertsPerRow;
        const b = a + 1;
        const c = a + 2;
        const d = a + 3;

        // Front face (CCW)
        indices.push(a, b, d);
        indices.push(a, d, c);

        // Back face (CW, using mirrored winding)
        const offset = frontVertexCount;
        indices.push(offset + c, offset + d, offset + a);
        indices.push(offset + a, offset + d, offset + b);
    }

    const vertID = new Uint8Array(frontVertexCount*2);
    for (let i = 0; i < frontVertexCount*2; ++i) {
      vertID[i] = i;
    }

    geometry.setAttribute('vertIndex', new THREE.Uint8BufferAttribute(vertID, 1));
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
  
    return geometry;
  }

  createGrassMaterial(){
    const grassPhongShaderMaterial = new THREE.MeshStandardMaterial({
        // depthPacking: THREE.RGBADepthPacking
        color: 0x005500,
        // side: THREE.DoubleSide,
        side: THREE.FrontSide,
        // wireframe: true,
        transparent: true,
        depthTest: true,
        depthWrite: true,
        roughness: 0.5,
        metalness: 0.15,
    })
    
    grassPhongShaderMaterial.onBeforeCompile = (shader) =>   
        {
            const windNoiseTex = this.assetLoader.textureLoader.load("/texture/windNoise256.png");
            windNoiseTex.wrapS = windNoiseTex.wrapT = THREE.RepeatWrapping;
            windNoiseTex.minFilter = THREE.LinearMipmapLinearFilter;
            windNoiseTex.magFilter = THREE.LinearFilter;

            shader.uniforms.uWindNoiseTex = { value: windNoiseTex };

            // shader.uniforms.uTime = planeInstanceShaderMaterial.uniforms.uTime.value
            shader.uniforms.uTime = customUniforms.uTime;
            shader.uniforms.uWindSpeed = customUniforms.uWindSpeed;
            shader.uniforms.uWindBendFactor = customUniforms.uWindBendFactor;
            shader.uniforms.uVisualizeWindNoise = customUniforms.uVisualizeWindNoise;
            shader.uniforms.uWindNoiseAZoom = customUniforms.uWindNoiseAZoom;
            shader.uniforms.uWindNoiseBZoom = customUniforms.uWindNoiseBZoom;
            shader.uniforms.uRotationAngle = customUniforms.uRotationAngle;
            
            shader.vertexShader = shader.vertexShader.replace(
                '#include <common>',
                `
                #include <common>
    
                uniform float uTime;
                uniform float uWindSpeed;
                uniform float uWindBendFactor;
                uniform vec2 uWindNoiseAZoom;
                uniform vec2 uWindNoiseBZoom;
                uniform float uRotationAngle;
                uniform sampler2D uWindNoiseTex;

                attribute vec3 a_instanceInfo; // x,z,bendDir
                attribute vec3 a_bladeParams;  // bend,height,tint
                attribute float a_phase;
    
                attribute float vertIndex;
    
                varying float vaBladeTint;
                varying float vWindAmplitude;
    
                varying vec3 vReNormal;
                varying vec3 vWorldPosition;  
    
                float inverseLerp(float minValue, float maxValue, float v) {
                return (v - minValue) / (maxValue - minValue);
                }
    
                float remap(float v, float inMin, float inMax, float outMin, float outMax) {
                float t = inverseLerp(inMin, inMax, v);
                return mix(outMin, outMax, t);
                }
    
                vec2 bezierCurve(vec2 start,vec2 end,vec2 anchor,float t){
                return mix( mix(start,anchor,t), mix(anchor,end,t) ,t);
                }
    
                mat4 rotateY(float angle) {
                    float c = cos(angle);
                    float s = sin(angle);
    
                    return mat4(
                        c, 0.0, s, 0.0,
                        0.0, 1.0, 0.0, 0.0,
                        -s, 0.0, c, 0.0,
                        0.0, 0.0, 0.0, 1.0
                    );
                }
                
                mat4 rotateZ(float angle) {
                    float c = cos(angle);
                    float s = sin(angle);
    
                    return mat4(
                        c, -s, 0.0, 0.0,
                        s, c, 0.0, 0.0,
                        0.0, 0.0, 1.0, 0.0,
                        0.0, 0.0, 0.0, 1.0
                    );
                }
    
                `
            )
        
            shader.vertexShader = shader.vertexShader.replace(
                '#include <beginnormal_vertex>',
                `
                #include <beginnormal_vertex>

                //unpacking attribs from batched attribs
                float a_bladeCordinateX = a_instanceInfo.x;
                float a_bladeCordinateY = a_instanceInfo.y;
                float a_bladeCordinateZ = a_instanceInfo.z;

                float a_bendFactor  = a_bladeParams.x;
                float a_bladeHeight = a_bladeParams.y;
                float a_bladeTint   = a_bladeParams.z;

                // Figure out vertex id, > GRASS_VERTICES is back side
                float vertID = mod(float(vertIndex), 20.);
    
                // 1 = front, -1 = back
                float zSide = -(floor(vertIndex / 20.) * 2.0 - 1.0);
    
                //-BLADE-CURVE-Y-NORMAL-CALCULATION----------------------------------------------------------------------------------------------------------------
                float halfWidth = 0.15; // Adjust this to match bladeWidth / 2
                float normalizedX = clamp(position.x / halfWidth, -1.0, 1.0);
    
                // Map -1 to 1 → -PI/2 to +PI/2
                float angle = normalizedX * 1.57;
    
                // Bend outward from center
                vec3 bentNormal = normalize(1. * vec3(
                    sin(angle),
                    0.5,
                    0.0
                ));
    
                objectNormal = bentNormal;
                vReNormal = normalize(objectNormal);
    
    
                // Apply the rotation matrix to the normal
                // Create the y rotation matrix
                mat4 rotationMatrix = rotateY(uRotationAngle);
    
                vReNormal += normalize(mat3(rotationMatrix) * normal);
    
                objectNormal = normalize(vReNormal);
    
                //-BLADE-BEND-Z-NORMAL-CALCULATION----------------------------------------------------------------------------------------------------------------
    
                float preCurrVertHeight = position.y;
                float preBendFactor = remap(a_bendFactor,0.,1.,0.2,0.8);
                float preBendHeight = (1. - preBendFactor) * a_bladeHeight;
    
                //fix this , causing stretch of the blade
                vec2 preEndPointCoordZ = vec2(0.0 - (a_bladeHeight-preBendHeight),preBendHeight);
    
                vec2 preCurvePosZ = bezierCurve(vec2(0.0,0.0),preEndPointCoordZ,vec2(0.0,preBendHeight),preCurrVertHeight);
                vec2 PreCurvePosZ_prev = bezierCurve(vec2(0.0,0.0),preEndPointCoordZ,vec2(0.0,preBendHeight),preCurrVertHeight - 0.01);
                
                vec2 zDirection = normalize(preCurvePosZ - PreCurvePosZ_prev);
                vec2 bendNormal2D = vec2(-zDirection.y, zDirection.x); // 90-degree rotate
                bentNormal = normalize(vec3(0.0, bendNormal2D.x, bendNormal2D.y));  // X=0, because bending in Y-Z
    
                vReNormal += bentNormal;
    
                objectNormal = normalize(vReNormal);
    
                //-WIND-NORMAL-CALCULATION----------------------------------------------------------------------------------------------------------------
                
                //passing to frag
                vaBladeTint = a_bladeTint;
                
                // mvPosition.y *= a_bladeHeight; //scale verts to their coresponding size
    
                float currVertHeight = position.y;
    
                // vec2 modelPositionXZ = a_instanceInfo.xy;
                // float windAmplitude = cnoise( ( modelPositionXZ * uWindNoiseAZoom) + vec2(uTime * uWindSpeed) ) ; // -1 to 1
                // windAmplitude += cnoise( ( modelPositionXZ * uWindNoiseBZoom) + vec2(uTime * uWindSpeed) ) * 0.5 ; // -1 to 1
                
                vec2 worldXZ = a_instanceInfo.xy * 0.25;   // 🔥 controls sweep scale

                float n1 = texture2D(
                  uWindNoiseTex,
                  worldXZ * 0.12 + vec2(uTime * uWindSpeed * 0.12)
                ).r;

                float n2 = texture2D(
                  uWindNoiseTex,
                  worldXZ * 0.35 - vec2(uTime * uWindSpeed * 0.22)
                ).r;

                float windAmplitude = (n1 + (n2 * 0.5));   // 🔥 CENTER + BOOST
    
                windAmplitude = remap(windAmplitude,-1.5,1.5,0.,1.) * uWindBendFactor;
                vWindAmplitude = windAmplitude;
    
                float bendHeight = (1. - windAmplitude) * a_bladeHeight;
    
                vec2 endPointCoordX = vec2(0.0 - (a_bladeHeight-bendHeight),bendHeight);
                vec2 endPointCoordZ = vec2(0.0 - (a_bladeHeight-bendHeight),bendHeight);
    
                vec2 curvePosX = bezierCurve(vec2(0.0,0),endPointCoordX,vec2(0.0,bendHeight),currVertHeight);
                vec2 curvePosX_prev = bezierCurve(vec2(0.0,0),endPointCoordX,vec2(0.0,bendHeight),currVertHeight - 0.01);
                vec2 curvePosZ = bezierCurve(vec2(0.0,0),endPointCoordZ,vec2(0.0,bendHeight),currVertHeight);
                vec2 curvePosZ_prev = bezierCurve(vec2(0.0,0),endPointCoordZ,vec2(0.0,bendHeight),currVertHeight - 0.01);
    
                vec3 curvePosXYZ = vec3(curvePosX.x, curvePosX.y, curvePosZ.y);
                vec3 curvePosXYZ_prev = vec3(curvePosX_prev.x, curvePosX_prev.y, curvePosZ_prev.y);
    
                vec3 tangent = normalize(curvePosXYZ - curvePosXYZ_prev);
                            
                // 4. Generate stable normal using Gram-Schmidt
                vec3 ref = abs(tangent.y) > 0.99 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
                vec3 windBendNormal = normalize(ref - dot(ref, tangent) * tangent);
    
                // vec3 up = vec3(0.0, -1.0, 0.0); // World up
                // vec3 bitangent = normalize(cross(tangent, up));
                // vec3 windBendNormal = normalize(cross(bitangent, tangent));
    
                // vReNormal += windBendNormal;
    
                // objectNormal = normalize(vReNormal);
    
                //-----------------------------------------------------------------------------------------------------------------
    
                
                `
            )
    
            shader.vertexShader = shader.vertexShader.replace(
                '#include <begin_vertex>',
                `
                #include <begin_vertex>
    
                //-APPLY-BLADE-CURVE-Y-----------------------------------------------------------------------------------------------------------------
    
                // Apply the rotation matrix from normal
                vec4 rotatedPosition = rotationMatrix * vec4(transformed, 1.0);
    
                transformed = rotatedPosition.xyz;
    
                //-APPLY-BLADE-BEND-Z---------------------------------------------------------------------------------------------------

                transformed.z += preCurvePosZ.x;
    
                //------------------------------------------------------------------------------------------------------------------------
    
                `
            )
    
            shader.vertexShader = shader.vertexShader.replace(
                '#include <project_vertex>',
                `
                //#include <project_vertex>
                
                
                //ground
                // transformed.y += sin(transformed.x * 1.0) / 4.0;
                // positon.z += cos(positon.y + 100.) / 3.0;
    
                vec4 mvPosition = vec4( transformed, 1.0 );
                 
                #ifdef USE_INSTANCING
                    mvPosition = instanceMatrix * mvPosition;
                #endif
    
                //-APPLY-WIND-BEND-Z---------------------------------------------------------------------------------------------------
                           
                mvPosition.x += curvePosX.x;
                mvPosition.y += curvePosX.y;
                mvPosition.z += curvePosZ.x;
    
                vWorldPosition = mvPosition.xyz;
    
                //------------------------------------------------------------------------------------------------------------------------
    
                mvPosition = modelMatrix * mvPosition;
                
                mvPosition = viewMatrix * mvPosition;
    
                gl_Position = projectionMatrix * mvPosition;
                `
            )
    
            shader.fragmentShader = shader.fragmentShader.replace(
                'uniform vec3 diffuse;',
                `
                
                uniform float uTime;
                uniform float uWindSpeed;
                uniform float uVisualizeWindNoise;
    
                varying float vaBladeTint;
                varying float vWindAmplitude;
    
                varying vec3 vReNormal;
                varying vec3 vWorldPosition;
    
                uniform vec3 diffuse;
                `
            );
            
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <color_fragment>',
                `
                #include <color_fragment>
                
                vec2 st = vWorldPosition.xy;
                
                vec3 lightGreen = vec3(0.01, 0.15, 0.03);
                vec3 darkGreen  = vec3(0.01, 0.1, 0.);
                vec3 groundBrown = vec3(0.051,0.025,0.);
                
                float windVisualShader = 0.0;
                vec3 grassTint = mix(darkGreen,lightGreen,vaBladeTint);
                
                windVisualShader = min(uVisualizeWindNoise,vWindAmplitude);
                
                vec3 grassShader = mix(groundBrown,grassTint,st.y + 0.7 );
                // vec3 grassShader = mix(grassTint,groundBrown,st.y * 0.7 );
                
                vec3 iColor = vec3(grassShader.xy,windVisualShader*0.5);
                
                diffuseColor.rgb = iColor;
                
                `
            );
            
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <normal_fragment_begin>',
                `
                #include <normal_fragment_begin>
                
                // float tcolor = 0.0;
                // vec3 nLight = normalize(vec3(5.0, 10.0, 5.0));
                // // Ensure lighting is same on back faces
                // // if (!gl_FrontFacing) {
                // if (dot(normal, nLight) < 0.0) {
                //     tcolor = 0.0;
                //     // normal = normalize(-vReNormal);
                // } else{
                //     tcolor = 1.0;
                //     // normal = normalize(vReNormal);
                // }
                `
            );
    
            shader.fragmentShader = shader.fragmentShader.replace(
                '#include <dithering_fragment>',
                `
                // // Calculate fake subsurface scattering (translucency)
                // vec3 lightDir = normalize(vec3(0.5, 1.0, 0.3)); // tweak this based on your scene's light
                // float translucency = max(dot(-lightDir, normal), 0.0);
                // vec3 glowColor = vec3(0.4, 1.0, 0.3); // pale green glow
    
                // // Mix with original output
                // diffuseColor.rgb += glowColor * pow(translucency, 1.5) * 0.5;
    
                // diffuseColor.rgb = vec3(normalize(normal) * 0.5 +0.5);
    
                // gl_FragColor.rgb = vec3(normal.x * 0.5 + 0.5,normal.y * 0.5 + 0.5,normal.z * 0.5 + 0.5);
                // gl_FragColor.b = tcolor;
                
                #include <dithering_fragment>
                `
            );
    
            // console.log(shader.vertexShader);
            // console.log(shader.fragmentShader);
    
            
            grassPhongShaderMaterial.userData.shader = shader;
    }

    return grassPhongShaderMaterial;
  }

  initGrass() {
          
      var dummy = new THREE.Object3D();
  
      let allPlanePosition = [];
      const rowCountX = CONFIG.isMobile ? 165 : 230
      const rowCountY = 200
      let numInstances = rowCountX * rowCountY; //40000
      const lowPolyVertCountY = 10;
      const lowPolyVertCountX = 10;
      const planeInstanceSizeY = 0.5;
      const planeInstanceSizeX = 0.125;
      // const planeInstanceGap = -0.05;
      const planeInstanceGap = 0.125;
      const grassInstanceScale = 0.2;
  
      // const lowGeom = new THREE.PlaneGeometry(planeInstanceSizeX, planeInstanceSizeY ,lowPolyVertCountX, lowPolyVertCountY);       // low detail
      const lowGeom = new THREE.IcosahedronGeometry(1, 0);       // low detail
  
      const translate = new THREE.Matrix4().makeTranslation(0, 0, 0); // move up 1 unit
      const euler = new THREE.Euler(Math.PI / 2, -Math.PI /2, 0);
      
      const bladeGeo = this.createGrassBladeGeometry(planeInstanceSizeX, planeInstanceSizeY,lowPolyVertCountY);

      // const instancedLowGeom = new THREE.InstancedBufferGeometry().copy(lowGeom);
      const instancedLowGeom = new THREE.InstancedBufferGeometry().copy(bladeGeo);      
  
      // Blade height per instance
      const bendFactors = new Float32Array(numInstances);
      const bladeTint = new Float32Array(numInstances);
      const bladeHeight = new Float32Array(numInstances);
      const bendDirection = new Float32Array(numInstances);
  
      for (let i = 0; i < numInstances; i++) {
          bendFactors[i] = Math.random(); // bend factor between 0.0 and 1.0
          bladeTint[i] = Math.random() * 0.5; // blade tint between 0.0 and 0.5
          bladeHeight[i] = (Math.random() + 1.5); // blade height between 0.5 and 1.5
          bendDirection[i] = (Math.random() > 0.5? -1:1); // blade height between
          // bendDirection[i] = 1; // blade height between
      }
      instancedLowGeom.setAttribute('a_bendFactor', new THREE.InstancedBufferAttribute(bendFactors, 1));
      instancedLowGeom.setAttribute('a_bladeTint', new THREE.InstancedBufferAttribute(bladeTint, 1));
      instancedLowGeom.setAttribute('a_bladeHeight', new THREE.InstancedBufferAttribute(bladeHeight, 1));
      // instancedLowGeom.setAttribute('a_bendDirection', new THREE.InstancedBufferAttribute(bendDirection, 1));
  
      this.grass = new THREE.InstancedMesh(instancedLowGeom, this.grassMat, numInstances);

      // this.grass = new THREE.InstancedMesh(instancedLowGeom, grassMat, numInstances);
      // this.grass = new THREE.InstancedMesh(instancedLowGeom, planeInstanceShaderMaterial, numInstances);
      // this.grass.castShadow = true;
      // this.grass.receiveShadow = true;
  
      const meshNormal = new THREE.Mesh(bladeGeo, this.grassMat);
      const helper = new VertexNormalsHelper( meshNormal, 1, 0xff0000 );
        
      let lowIndex = 0;
  
      const bladeCordinateX = new Float32Array(numInstances);
      const bladeCordinateY = new Float32Array(numInstances);
      const bladeCordinateZ = new Float32Array(numInstances);
  
      for ( var i = 0; i < rowCountX ; i++ ) {
          var xStaticPosition = -(planeInstanceSizeX + planeInstanceGap) * (i - (rowCountX / 2) - (rowCountX /12)); 
          for ( var j = 0; j <rowCountY ; j++ ) {
              var zStaticPosition = -(planeInstanceSizeX + planeInstanceGap) * (j - (rowCountY /2));

              var yStaticPosition = Math.sin((xStaticPosition / 5.0) + customUniforms.grassOffsetX.value) * 1.0  
                                  + Math.cos((zStaticPosition / 4.0) + customUniforms.grassOffsetY.value) * 0.5;
              
          // pos.z += sin((pos.x / 5.) + 10.) * 1.;
          // pos.z += cos((pos.y / 4.) + 5.) * 0.5;

              let currentPosition = new THREE.Vector3(xStaticPosition - (Math.random()- 0.5),
                                                      0 + yStaticPosition,
                                                      zStaticPosition - (Math.random()- 0.5));
              // let currentPosition = new THREE.Vector3(0,0, 0);
              // let currentPosition = new THREE.Vector3(0,1, 0);
              
              bladeCordinateX[lowIndex] = currentPosition.x;
              bladeCordinateY[lowIndex] = currentPosition.z;
              bladeCordinateZ[lowIndex] = currentPosition.y;
              
              dummy.position.set(currentPosition.x, currentPosition.y, currentPosition.z);
              dummy.rotation.y = Math.PI * Math.random();
              dummy.updateMatrix();
  
              this.grass.setMatrixAt(lowIndex++, dummy.matrix); 
          }
          this.grass.count = lowIndex;
  
          this.grass.instanceMatrix.needsUpdate = true;
      }
  
      instancedLowGeom.setAttribute('a_bladeCordinateX', new THREE.InstancedBufferAttribute(bladeCordinateX, 1));
      instancedLowGeom.setAttribute('a_bladeCordinateY', new THREE.InstancedBufferAttribute(bladeCordinateY, 1));
      instancedLowGeom.setAttribute('a_bladeCordinateZ', new THREE.InstancedBufferAttribute(bladeCordinateZ, 1));
      
      const a_instanceInfo = new Float32Array(numInstances * 3);
      const a_bladeParams = new Float32Array(numInstances * 3);

      for (let i = 0; i < numInstances; i++) {

        // pack world coords + bendDir
        a_instanceInfo[i * 3 + 0] = bladeCordinateX[i];   // world X
        a_instanceInfo[i * 3 + 1] = bladeCordinateY[i];   // world Z
        a_instanceInfo[i * 3 + 2] = bladeCordinateZ[i];     // bend direction (-1 / 1)

        // pack blade params
        a_bladeParams[i * 3 + 0] = bendFactors[i];        // bendFactor
        a_bladeParams[i * 3 + 1] = bladeHeight[i];        // bladeHeight
        a_bladeParams[i * 3 + 2] = bladeTint[i];          // tint
      }

      instancedLowGeom.setAttribute(
        'a_instanceInfo',
        new THREE.InstancedBufferAttribute(a_instanceInfo, 3)
      );

      instancedLowGeom.setAttribute(
        'a_bladeParams',
        new THREE.InstancedBufferAttribute(a_bladeParams, 3)
      );

      // Clear the previous instance matrices
      this.grass.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.grass.renderOrder = 1;
            
      this.sceneManager.scene.add(this.grass);
      // scene.add(meshNormal);
      // scene.add( helper );
  }

  initSky(scene){
        // 🎨 tweak these
    const noiseTex = this.assetLoader.textureLoader.load("/texture/windNoise256.png");
    noiseTex.wrapS = noiseTex.wrapT = THREE.RepeatWrapping;
    noiseTex.minFilter = THREE.LinearFilter;
    noiseTex.magFilter = THREE.LinearFilter;
    
    const uniforms = {
      topColor:    { value: new THREE.Color('#87b5ff') }, // soft peach/orange at zenith
      btwnColor:    { value: new THREE.Color('#75cafc') }, // mid
      middleColor: { value: new THREE.Color('#ffe9bb') }, // warm sunrise glow
      bottomColor: { value: new THREE.Color('#ffdca8') }, // cool horizon blue
      sunColor:    { value: new THREE.Color('#f5c38b') }, // faint sun tint
      sunDir:      { value: new THREE.Vector3(0.0, -0.7, -10.0).normalize() }, // where the sun is in the sky
      sunStrength: { value: 1. }, // brightness of sun disc
      sunSize:     { value: 0.005 },   // radius of sun disc

      uNoise:      { value: noiseTex },
      uCloudSpeed: { value: 0.002 },
      uCloudScale: { value: 4 },
      uCloudIntensity: { value: 0.6 },
      uTime:       { value: 0 },

      uNight:      { value: 0. },   // 0 day, 1 night
      uStarDensity:{ value: 300 }
    };

    const skyMaterial = new THREE.ShaderMaterial({
      uniforms,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,

      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPosition = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        varying vec3 vWorldPosition;

        uniform vec3 topColor;
        uniform vec3 btwnColor;
        uniform vec3 middleColor;
        uniform vec3 bottomColor;

        uniform vec3 sunColor;
        uniform vec3 sunDir;
        uniform float sunStrength;
        uniform float sunSize;
                
        uniform sampler2D uNoise;
        uniform float uTime;
        uniform float uCloudSpeed;
        uniform float uCloudScale;
        uniform float uCloudIntensity;

        uniform float uNight;
        uniform float uStarDensity;

        float random(vec2 st){
            return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }

        float hash12(vec2 p)
        {
            vec3 p3 = fract(vec3(p.xyx) * 0.1031);
            p3 += dot(p3, p3.yzx + 33.33);
            return fract((p3.x + p3.y) * p3.z);
        }

        float starShape(vec2 uv)
        {
            uv = abs(uv - 0.5);

            float vertical = smoothstep(0.2, 0.0, uv.x);
            float horizontal = smoothstep(0.2, 0.0, uv.y);

            return max(vertical, horizontal);
        }

        void main() {

          vec3 dir = normalize(vWorldPosition);

          // --- SKY GRADIENT ---
          float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);
          vec3 grad = (h < 0.5)
            ? mix(bottomColor, middleColor, smoothstep(0.0, 0.5, h))
            : (h < 0.8) ? mix(middleColor, btwnColor,  smoothstep(0.5, 0.8, h)) : mix(btwnColor, topColor,  smoothstep(0.75, 1.0, h));


          // --- SUN ---
          float sunDot = clamp(dot(dir, normalize(sunDir)), 0.0, 1.0);
          float disc = smoothstep(sunSize, sunSize * 0.2, 1.0 - sunDot);
          disc = pow(disc, 3.0);

          // --- CLOUDS ---

          // safe atan
          float angle = atan(dir.z * 1.5, max(abs(dir.x), 1e-6));

          // wrap to 0–1
          vec2 uv = vec2(
            angle / (2.0 * 3.14159) + .0,
            h  // use height instead of recomputing
          );

          
          // animate
          uv += vec2(uTime * uCloudSpeed);
          
          // scale
          uv *= uCloudScale;
          
          // sample noise
          float n = texture2D(uNoise, uv).r;
          
          // base cloud mask
          float clouds = smoothstep(0.65, 0.75, n);
          
          // clouds only toward topColor region
          float topMask = smoothstep(0.55, 1.0, h);
          clouds *= topMask;
          
          // --- STARS ---
          // spherical mapping
          float phi = atan(dir.z, dir.x);
          float theta = acos(dir.y);

          vec2 starUV = vec2(
              phi / (2.0 * 3.1415926) + 0.5,
              theta / 3.1415926
          );

          starUV *= uStarDensity;

          vec2 cell = floor(starUV);
          vec2 local = fract(starUV);

          float rnd = texture2D(uNoise, fract(cell * 0.013)).r;
          rnd += texture2D(uNoise, fract(cell * 0.037 + 0.17)).r * 0.5;
          rnd = fract(rnd);

          // star appears if random threshold passes
          float heightFactor = pow(h, 6.0);
          float density = mix(1., 0.998, heightFactor);
          float starMask = step(density, rnd);

          // cross shape
          float star = starShape(local) * starMask;

          float twinkle = sin(uTime * 3.0 + rnd * 20.0) * 0.5 + 0.5;
          star *= mix(0.7, 1.0, twinkle);

          vec3 starColor = vec3(star);
          
          // final blend
          vec3 cloudColor = vec3(1.0);

          vec3 dayColor = mix(grad, cloudColor, clouds * uCloudIntensity);
          dayColor += sunColor * disc * sunStrength;

          // night sky base
          vec3 nightSky = mix(vec3(0.02,0.03,0.08), vec3(0.05,0.07,0.15), h);
          nightSky = mix(nightSky, cloudColor * 0.3, clouds * uCloudIntensity);

          vec3 moonDir = normalize(sunDir);

          // moon size
          float moonRadius = 0.03;

          // build moon plane basis
          vec3 right = normalize(cross(vec3(0.0,1.0,0.0), moonDir));
          vec3 up = normalize(cross(moonDir, right));

          // project sky direction onto moon plane
          vec2 moonUV = vec2(
              dot(dir, right),
              dot(dir, up)
          ) / moonRadius;

          // outer moon circle
          float outer = 1.0 - smoothstep(0.98, 1.0, length(moonUV));

          // inner offset circle (creates crescent)
          vec2 offset = vec2(0.55, 0.0);
          float inner = 1.0 - smoothstep(0.98, 1.0, length(moonUV + offset));

          // final crescent
          float crescent = clamp(outer - inner, 0.0, 1.0);

          // moon color
          vec3 moonColor = vec3(0.95, 0.95, 0.9);

          // expanded crescent for glow
          float outerGlow = 1.0 - smoothstep(1.2, 2.6, length(moonUV));
          float innerGlow = 1.0 - smoothstep(1.0, 2.1, length(moonUV + offset + vec2(1.2, 0.0)));

          // glow follows crescent shape
          float crescentGlow = clamp(outerGlow - innerGlow, 0.0, 1.0);

          // add glow
          nightSky += vec3(0.6, 0.7, 1.0) * crescentGlow * 0.25;

          // add to sky
          nightSky += moonColor * crescent * 2.0;

          // add stars
          nightSky += starColor;

          vec3 color = mix(dayColor, nightSky, uNight);



          gl_FragColor = vec4(color, 1.0);
        }
      `
    });

    this.skyMaterial = skyMaterial;

    // giant inside-out sphere
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(200, 32, 16),
      skyMaterial
    );
    
    scene.add(sky);
  }

  createFireflyMaterial(){
    const windNoiseTex = this.assetLoader.textureLoader.load("/texture/windNoise256.png");
    windNoiseTex.wrapS = windNoiseTex.wrapT = THREE.RepeatWrapping;
    windNoiseTex.minFilter = THREE.LinearMipmapLinearFilter;
    windNoiseTex.magFilter = THREE.LinearFilter;

    
    const butterflyTexture = this.assetLoader.textureLoader.load('texture/butterfly.png');

    return new THREE.ShaderMaterial({
      transparent:true,
      depthWrite:true,
      depthTest: true,
      blending:THREE.NormalBlending,

      uniforms:{
          uTime:{value:0.2},
          uNoise:{value: windNoiseTex},
          uButterfly: { value: 1. },
          uTexture: { value: butterflyTexture }
      },

      vertexShader:`
        uniform float uTime;
        uniform float uButterfly;
        uniform sampler2D uNoise;

        attribute vec3 aOffset;
        attribute float aScale;

        varying float vDistance;
        varying float vFlicker;
        varying vec2 vUv;
        varying float vButterflyType;

        mat3 rotX(float a){
            float s = sin(a);
            float c = cos(a);

            return mat3(
                1.0, 0.0, 0.0,
                0.0,  c , -s ,
                0.0,  s ,  c
            );
        }

        mat4 rotateY(float angle) {
            float c = cos(angle);
            float s = sin(angle);

            return mat4(
                c, 0.0, s, 0.0,
                0.0, 1.0, 0.0, 0.0,
                -s, 0.0, c, 0.0,
                0.0, 0.0, 0.0, 1.0
            );
        }

        uvec4 murmurHash42(uvec2 src) {
          const uint M = 0x5bd1e995u;
          uvec4 h = uvec4(1190494759u, 2147483647u, 3559788179u, 179424673u);
          src *= M; src ^= src>>24u; src *= M;
          h *= M; h ^= src.x; h *= M; h ^= src.y;
          h ^= h>>13u; h *= M; h ^= h>>15u;
          return h;
        }

        // 4 outputs, 2 inputs
        vec4 hash42(vec2 src) {
          uvec4 h = murmurHash42(floatBitsToUint(src));
          return uintBitsToFloat(h & 0x007fffffu | 0x3f800000u) - 1.0;
        }

        void main(){

          vUv = uv;
          
          vec3 movement = vec3(0.0);
          vec2 uvNoise = aOffset.xz * 2.2 + vec2(uTime * 0.005);

          vec2 flow = texture2D(uNoise, uvNoise).rg * 2.0 - 1.0;

          movement.x += flow.x * 10.6;
          movement.z += flow.y * 10.6;

          float lift = texture2D(uNoise, uvNoise * 5. + 3.0).r;
          movement.y += (lift - 0.5) * 2.;

          vec3 posButterfly = position ;

          vec4 butterflyHashVal = hash42(aOffset  .xz);
          vButterflyType = butterflyHashVal.w;
          
          float BTFY_SCALE = mix(0.25, 0.55, butterflyHashVal.z);
          posButterfly *= BTFY_SCALE;

          vec4 sampledNoise = texture2D(uNoise,vec2(uTime * 0.5 ,uTime)) * 0.35;
          float sampledHeightRandom = sampledNoise.w;

          posButterfly = rotX(90.0) * posButterfly; // circular movement

          float yOffset = sampledHeightRandom
            + (sin(abs(position.x * 3.) + (uTime * (15. + (10. * butterflyHashVal.w) ))) * 0.3)
            + (cos((uTime * (15. + (10. * butterflyHashVal.w) )) + 1.57) * 0.3);

          posButterfly.y += yOffset;

          float loop = (butterflyHashVal.y * 30.) * sin(movement.x)*cos(movement.x*2.) + sin(movement.x*3.)*cos(movement.x*4.);

          mat4 matRotateY = rotateY(loop); 

          posButterfly *= mat3(matRotateY);

          // posButterfly.xyz += bugsOffset.xyz;
          posButterfly.xyz += movement.xyz * vec3(1.3,0.,1.3);
          posButterfly.xyz += aOffset.xyz + vec3(0.,0.3,0.);



          //---------------------------------------------
          vec3 posFirefly = position;

          posFirefly *= 0.1;
          posFirefly += aOffset;

          posFirefly.x += sin(uTime * 0.5) * 0.5; // vertical bobbing

          posFirefly += movement;

          vFlicker = sin(uTime * 2.0 + aOffset.x * 8.0 + aOffset.z * 5.0) * 0.5 + 0.5;

          vec3 pos = mix(posFirefly, posButterfly, uButterfly);

          vec4 mvPosition = modelViewMatrix * vec4(pos,1.0);

          vec3 billboard = vec3(position.xy * aScale,0.0);

          mvPosition.xyz += billboard;

          vDistance = -mvPosition.z;

          gl_Position = projectionMatrix * mvPosition;
        }
      `,

      fragmentShader:`
        varying float vDistance;
        varying float vFlicker;
        varying vec2 vUv;

        uniform float uButterfly;
        uniform sampler2D uTexture;

        void main(){
          vec3 color = vec3(1.0);
          float alpha = 1.0;

          vec4 texColor = texture2D(uTexture, vUv);

          vec2 uv = vUv - 0.5;
          float d = length(uv);

          float glow = smoothstep(0.5,0.0,d);
          float core = smoothstep(0.2,0.0,d);

          vec3 coreColor = vec3(1.0,0.95,0.6);
          vec3 glowColor = vec3(1.0,0.85,0.3);

          vec3 fireFlyColor = mix(glowColor, coreColor, core);

          float fireFlyAlpha = glow * (0.5 + vFlicker * 0.8);
          float distFade = smoothstep(100.0,10.0,vDistance);

          fireFlyAlpha *= distFade;

          vec4 fireFlyColorVec4 = vec4(fireFlyColor, fireFlyAlpha);

          vec4 finalColor = mix(texColor, fireFlyColorVec4, 1. - uButterfly);

          gl_FragColor = finalColor;
        }
      `
    });
  }

  createFireflyGeometry(fireflyCount = 100){

    const geometry = new THREE.PlaneGeometry(0.35,0.35,2,2);

    const offsets = new Float32Array(fireflyCount * 3);
    const scales = new Float32Array(fireflyCount);

    for(let i=0;i<fireflyCount;i++){

      const i3 = i*3;
      const radius = 20;

      const r = Math.pow(Math.random(),1) * radius;
      const angle = Math.random() * Math.PI * -1;

      offsets[i3+0] = Math.cos(angle) * r;
      offsets[i3+2] = Math.sin(angle) * r;
      offsets[i3+1] = Math.random()*2;

      scales[i] = Math.random();
    }

    geometry.setAttribute(
      "aOffset",
      new THREE.InstancedBufferAttribute(offsets,3)
    );

    geometry.setAttribute(
      "aScale",
      new THREE.InstancedBufferAttribute(scales,1)
    );

    return geometry;
  }

  initFireflies(count = 100){
    const fireflyGeo = this.createFireflyGeometry(count);

    const fireflies = new THREE.InstancedMesh(
      fireflyGeo,
      this.fireflyMat,
      count
    );

    fireflies.frustumCulled = false;
    fireflies.renderOrder = 5;

    fireflies.position.copy(this.sceneManager.camera.position ).add(new THREE.Vector3(-5.,-3.0,-7.5));

    return fireflies;
  }

  initFlower = () => {
      this.assetLoader.loadGLTF('/model/flower5/flower.glb', (gltf) => {    
          
          const flowerBunch = gltf.scene.children[0].children[0];
          
          const flowers = [];
          for(let i = 0; i < flowerBunch.children.length; i++){
              const flower = flowerBunch.children[i];
              if(!["Flower 3","Flower 2","Flower 1","Dandelion 3","Dandelion 2","Dandelion 1"].includes(flower.userData.name)) continue;
            
              flower.castShadow = true
              flower.receiveShadow = true
              flower.traverse((child) => {
                  if (child.isMesh) {
                      child.castShadow = true
                      child.receiveShadow = true
                      child.material.needsUpdate = true
                  }
              })
              flowers.push(flower);
          }
          
          const instanceCount = 6;

          for(let i = 0; i < flowers.length; i++){

              this.createInstances(flowers[i].children[0].geometry,
                              flowers[i].children[0].material,
                              instanceCount);
          }

      })
  }

  
  createInstances = (geometry, material,instance) => {
      // console.log(geometry, material,instance);
      
      const instancedMesh = new THREE.InstancedMesh(geometry, material, instance);
      instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // will be updated every frame
      instancedMesh.castShadow = true;
      instancedMesh.receiveShadow = true;

      const dummy = new THREE.Object3D();

      for (let i = 0; i < instance; i++) {
          const size = 0.27;
          const x = this.sceneManager.camera.position.x + (Math.random() - 0.5) * 40;
          const z = this.sceneManager.camera.position.z + (Math.random() - 1.) * 20;
          const y = 0;

          dummy.position.set(x, y, z);
          dummy.rotation.x = -Math.PI / 2
          dummy.rotation.z = Math.random() * Math.PI
          dummy.scale.set(size, size, size);

          dummy.updateMatrix();
          instancedMesh.setMatrixAt(i, dummy.matrix);
      }

      this.sceneManager.scene.add(instancedMesh);
  }


  async init() {
    // Set total assets to load
    this.loadingManager.setTotal(1)

    const axesHelper = new THREE.AxesHelper( 50 );
    axesHelper.position.set(0,3,0);
    axesHelper.renderOrder = 10;
    // this.sceneManager.scene.add( axesHelper );
    
    // Load assets
    await this.loadAssets()

    this.initGrass();

    this.initSky(this.sceneManager.scene);

    this.fireflies = this.initFireflies(100);
    
    this.sceneManager.scene.add(this.fireflies);
    
    // Create scene content
    this.createScene()
    
    // Start animation loop
    this.animate()
  }

  async loadAssets() {
    // Example: Load environment map
    // const envMap = this.assetLoader.loadCubeMap([
    //   '/env/px.png', '/env/nx.png',
    //   '/env/py.png', '/env/ny.png',
    //   '/env/pz.png', '/env/nz.png'
    // ], () => {
    //   this.loadingManager.increment('Environment loaded')
    // })
    
    
    this.assetLoader.loadGLTF('/model/tree.glb', (gltf) => {
      gltf.scene.children.forEach((child) => {

      })
      const tree = gltf.scene.children[0];
      const scale = 0.015;
      tree.scale.set(scale,scale,scale);
      tree.position.set(-7,1.5,7);
      this.sceneManager.scene.add(tree);
      this.loadingManager.increment('tree...')
    })
        
    this.initFlower();


    this.loadingManager.increment('Assets loaded')
  }

  createScene() {
    const geometry = new THREE.BoxGeometry(1, 1, 1)
    const material = new THREE.MeshStandardMaterial({ color: 0x00ff00 })
    const cube = new THREE.Mesh(geometry, material)
    cube.castShadow = true
    cube.receiveShadow = true
    // this.sceneManager.scene.add(cube)
    
    // Example: Add a plane
    const planeMaterial = new THREE.MeshStandardMaterial({ color: 0x808080, wireframe:true})
    const planeGeometry = new THREE.PlaneGeometry(45, 45,10,10)

    const grassMat = new THREE.ShaderMaterial({

      uniforms: {
        uHeightScale: { value: 3.0 }
      },

      vertexShader: `
        uniform float uHeightScale;

        varying vec2 vUv;

        void main() {

          vUv = uv; // pass UVs to fragment if needed

          vec3 pos = position;

          pos.z += sin((pos.x / 5.) + 10.) * 1.;
          pos.z += cos((pos.y / 4.) + 20.75) * 0.5;

          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,

      fragmentShader: `
        varying vec2 vUv;

        void main() {
          float n = fract(sin(dot(vUv, vec2(12.9898,78.233))) * 43758.5453);
          gl_FragColor = vec4(vec3(0.25, 0.15, 0.07) * (0.9 + n*0.1), 1.0);

        }
      `,

      side: THREE.DoubleSide
    });

    const plane = new THREE.Mesh(planeGeometry, grassMat)
    plane.position.set(0,-0.25,0);
    plane.rotation.x = -Math.PI * 0.5
    plane.receiveShadow = true
    this.sceneManager.scene.add(plane)
  }

  animate() {
    if (this.sceneManager.stats) {
      this.sceneManager.stats.begin()
    }

    const delta = this.sceneManager.update()

    const elapsedTime = clock.getElapsedTime()

    this.skyMaterial.uniforms.uTime.value = clock.getElapsedTime();

    if(this.fireflyMat){
       this.fireflyMat.uniforms.uTime.value = elapsedTime;
    }
    
    if( 
      this.grassMat &&
      this.grassMat.userData &&
      this.grassMat.userData.shader
    )   
    {
      this.grassMat.userData.shader.uniforms.uTime.value = elapsedTime
      this.grassMat.needsUpdate = true;
    }
    customUniforms.uTime.value = elapsedTime
    customUniforms.uWindSpeed.value = customUniforms.uWindSpeed.value
    customUniforms.uWindBendFactor.value = customUniforms.uWindBendFactor.value
    customUniforms.uVisualizeWindNoise.value = customUniforms.uVisualizeWindNoise.value
    customUniforms.uWindNoiseAZoom.value = customUniforms.uWindNoiseAZoom.value
    customUniforms.uWindNoiseBZoom.value = customUniforms.uWindNoiseBZoom.value
    
    // Update camera controller
    if (this.cameraController) {
      this.cameraController.update(delta)
    }
    
    // Your animation logic here
    
    this.sceneManager.render()

    if (this.sceneManager.stats) {
      this.sceneManager.stats.end()
    }

    window.requestAnimationFrame(() => this.animate())
  }
}

//=============================================================================
// START APPLICATION
//=============================================================================

const app = new App()